import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * The one place the UI learns what anything costs.
 *
 * The subscription price appears on the front page, in the checkout cards, in the
 * for-investors earnings table, in the investor handbook's worked example and in the
 * structured data crawlers read. Every one of those used to hold its own literal,
 * which meant raising the price was a five-file change and the fifth was always
 * missed. Now the admin edits one field and each of those surfaces re-reads it.
 *
 * Fetched from the public endpoint rather than /billing/config so the logged-out
 * marketing pages can use the same numbers as the signed-in ones — a visitor seeing
 * a different price from the one they are charged is the exact failure this prevents.
 *
 * Cached at module scope: several components on one page ask for it, and they should
 * produce one request between them, not one each.
 */
let cached = null;
let inflight = null;

export function loadPricing() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = api
      .publicPricing()
      .then((r) => {
        cached = r.data;
        return cached;
      })
      .catch((e) => {
        // Clear the slot so a network blip doesn't leave every later caller waiting
        // on a promise that already rejected.
        inflight = null;
        throw e;
      });
  }
  return inflight;
}

/** Lets a test or a settings save force the next read to go back to the server. */
export function resetPricing() {
  cached = null;
  inflight = null;
}

/**
 * `enabled: false` skips the request entirely. Pass the payments switch from
 * useAuth().features: while payments are off the endpoint answers 404, and a page
 * that is not going to show a price has no reason to ask for one.
 */
export default function usePricing({ enabled = true } = {}) {
  const [pricing, setPricing] = useState(cached);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    loadPricing()
      .then((p) => alive && setPricing(p))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [enabled]);
  return pricing;
}

/**
 * Price of one plan, or null while it is still loading.
 *
 * Null rather than a fallback constant on purpose: a hardcoded default is how a
 * stale price gets shown confidently, and "nothing yet" is the honest state. Callers
 * render a placeholder for the fraction of a second it takes to arrive.
 */
export function planPrice(pricing, plan) {
  const v = pricing?.plans?.[plan]?.gross_eur;
  return typeof v === "number" ? v : null;
}
